import type { Biome, BiomeMap, VoxelWorld } from "../../types";
import { createSeededRandom, findFlatStructureSpot } from "./placement";
import { NETHER_STRUCTURES, OVERWORLD_STRUCTURES } from "./registry";
import type { StructureDefinition, StructurePlacement } from "./types";

function placeFirstAvailable(
    definitions: readonly StructureDefinition[],
    world: VoxelWorld,
    heights: number[][],
    rng: () => number,
    acceptsCell?: (x: number, z: number) => boolean,
): StructurePlacement | null {
    if (definitions.length === 0) return null;
    const start = Math.floor(rng() * definitions.length);

    for (let index = 0; index < definitions.length; index++) {
        const definition = definitions[(start + index) % definitions.length];
        const spot = findFlatStructureSpot(
            world,
            heights,
            definition.surfaces,
            definition.footprint,
            rng,
            acceptsCell,
        );
        if (!spot) continue;
        definition.build(world, spot);
        return { id: definition.id, spot };
    }

    return null;
}

export function placeStructures(
    world: VoxelWorld,
    heights: number[][],
    biomes: BiomeMap,
    seed: number,
): StructurePlacement | null {
    const biome: Biome = biomes.biome[0][0];
    return placeFirstAvailable(
        OVERWORLD_STRUCTURES[biome],
        world,
        heights,
        createSeededRandom(seed * 17.31 + 4001),
        (x, z) => biomes.biome[x][z] === biome,
    );
}

export function placeNetherStructure(
    world: VoxelWorld,
    heights: number[][],
    seed: number,
): StructurePlacement | null {
    return placeFirstAvailable(
        NETHER_STRUCTURES,
        world,
        heights,
        createSeededRandom(seed * 23.17 + 9001),
    );
}

export { StructureId } from "./types";
export type {
    StructureBuilder,
    StructureDefinition,
    StructureFootprint,
    StructurePlacement,
    StructureSpot,
} from "./types";
